import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { MoneyDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/money-dto';
import { PayablesResponse } from '../../model/models';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';

export class GetInvoiceSummaryRequest extends Mk2Request {
    buyerKeys?: string[];
    sellerKeys?: string[];

    constructor(initial?: Partial<GetInvoiceSummaryRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class GetInvoiceSummaryResponse extends Mk2Response {
    invoiceSummary?: InvoiceSummaryDto;
    message?: string;
    ok?: boolean;

    constructor(initial?: Partial<GetInvoiceSummaryResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

// The buyer invoices page needs additional "flex term" fields compared to what legacy has.
export interface InvoiceSummaryDto {
    totalEarlyPaid?: MoneyDto;
    earlyPaidInvoiceCount?: number;
    payLaterInvoiceCount?: number;
    totalEligible?: MoneyDto;
    eligibleInvoiceCount?: number;
    totalIneligible?: MoneyDto;
    ineligibleInvoiceCount?: number;
    totalOpen?: MoneyDto;
    totalOpenInvoiceCount?: number;
}

@Injectable({ providedIn: 'root' })
export class GetInvoiceSummaryService extends ApiService<
    GetInvoiceSummaryRequest,
    GetInvoiceSummaryResponse
> {
    public requestType = GetInvoiceSummaryRequest;
    public responseType = GetInvoiceSummaryResponse;
    public endpointName = 'BuyerAccount/AccountSummary/GetInvoiceSummary';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/AccountSummary/GetInvoiceSummary';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetInvoiceSummaryRequest
    ): { query: string } | GetInvoiceSummaryRequest {
        if (this._isPayLaterEnabled) {
            return {
                query: `{${jsonToGraphQLQuery({
                    payables: {
                        __args: { input: { companyFilter: {} } },
                        totals: {
                            uncollectedFundingAmount: { cents: true, currencyCode: true },
                            uncollectedEarlyPaidPayableCount: true,
                            uncollectedPayLaterPayableCount: true,
                            earlyFundingEligiblePayableCount: true,
                            earlyFundingEligibleAmount: { cents: true, currencyCode: true },
                            openIneligiblePayableCount: true,
                            openIneligibleAmount: { cents: true, currencyCode: true },
                            openPayableCount: true,
                            openAmount: { cents: true, currencyCode: true }
                        }
                    }
                })}}`
            };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | GetInvoiceSummaryResponse
    ): GetInvoiceSummaryResponse {
        if (this._isPayLaterEnabled) {
            const { totals } = (res as GraphQLResponse<PayablesResponse>).data.payables;

            return new GetInvoiceSummaryResponse({
                ok: true,
                invoiceSummary: {
                    totalEarlyPaid: totals.uncollectedFundingAmount,
                    earlyPaidInvoiceCount: totals.uncollectedEarlyPaidPayableCount,
                    payLaterInvoiceCount: totals.uncollectedPayLaterPayableCount,
                    eligibleInvoiceCount: totals.earlyFundingEligiblePayableCount,
                    totalEligible: totals.earlyFundingEligibleAmount,
                    ineligibleInvoiceCount: totals.openIneligiblePayableCount,
                    totalIneligible: totals.openIneligibleAmount,
                    totalOpenInvoiceCount: totals.openPayableCount,
                    totalOpen: totals.openAmount
                }
            });
        }
        return res as GetInvoiceSummaryResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
